import { FunctionComponent, useCallback } from "react";
import { render } from "react-dom";
import { ProgressBar, StyleProvider, Theme } from "@animech/ui";
import { ARButton, ARProvider, ARProviderProps } from "@animech/ar";
import {
	joinPaths,
	makeResourceUrl,
	resolveProperty,
} from "@animech/axiom-utility";
import {
	SceneEngine,
	SceneEngineOptions,
} from "@animech/axiom-scene-utilities";
import { SceneBuilder } from "@animech/axiom-scene-builder";
import { View } from "@animech/camera";
import { AssetRegistry, BoundingBox, Entity, Vec3 } from "playcanvas";
import {
	cameraSettings,
	scenePath,
	viewerCameraMaxDistancePadding,
	viewerCameraPaddingFactor,
	viewerInitialOrbit,
} from "./settings";
import * as Styled from "./viewer-application.style";
import { ViewerCameraFitter } from "./viewer-camera-fitter";
import { ProgressiveAA } from "src/scene/scripts/progressive-aa";
import { initSceneEngine, webgpuRequested } from "src/scene/graphics-device";
import { config as appConfig } from "src/core/application";
import { Configurator, isProperty, makeConfigurator } from "src/utility";
import { Definitions, root } from "src/definitions";
import { CameraSystem } from "src/scene/camera-system";
import { GlobalStyle } from "src/ui/style";
import { ARRootWrapper } from "src/ui/components/app";
import {
	ConfigurationValue,
	GetConfigurationValue,
	Property,
	SceneStructure,
	SceneTree,
} from "src/common";
import { config as sceneConfig } from "src/scene/scene-manager";
import { queryClient } from "src/api";
import { ApiError, openRequest } from "src/api/endpoints";
import { OpenResponse } from "src/api/schemas";
import {
	OutgoingMessageEventType,
	PostMessageHandler,
} from "src/postmessage-handler";
import { _migrateAndDeserialize } from "src/core/configuration-migrations/deserializer";
import { restoreStoredConfiguration } from "src/utility/restore-stored-configuration";

export type ViewerApplicationArgs = {
	domRoot: string;
	resourceBaseUrl: string;
	theme: Theme;
	articleNumber: string | null;
	designTemplateId: string | null;
	guid: string | null;
	arConfig?: ARProviderProps;
	setupProductFromArticleNumber: (
		articleNumber: string,
		configurator: Configurator,
	) => string | undefined;
};

export class ViewerApplication {
	private _canvas = document.createElement("canvas");
	private _configurator: Configurator;
	private _sceneEngine: SceneEngine;
	private _domRoot: string;
	private _theme: Theme;
	private _setupProductFromArticleNumber: ViewerApplicationArgs["setupProductFromArticleNumber"];
	private _arConfig?: ARProviderProps;
	private _rootEntity?: Entity;
	private _arExportModel?: Entity;
	private _progressiveAA?: ProgressiveAA;
	private _postMessageHandler: PostMessageHandler;
	private _cameraFitter?: ViewerCameraFitter;

	constructor({
		domRoot,
		resourceBaseUrl,
		theme,
		articleNumber,
		designTemplateId,
		guid,
		setupProductFromArticleNumber,
		arConfig,
	}: ViewerApplicationArgs) {
		document.body.style.background = "transparent";

		this._domRoot = domRoot;
		this._theme = theme;
		this._setupProductFromArticleNumber = setupProductFromArticleNumber;
		this._arConfig = arConfig;
		this._sceneEngine = new SceneEngine();
		this._configurator = makeConfigurator(root, Definitions.Rule.rules, {
			valueAdjustment: "keep-first",
		});
		this._postMessageHandler = new PostMessageHandler({
			onIncomingMessageCallback: () => {},
		});

		if (!designTemplateId && !articleNumber) {
			this._renderError(
				"Missing required query parameters. Provide 'designtemplate' or 'article'.",
			);
			return;
		}

		if (designTemplateId && !guid) {
			this._renderError(
				"Missing required 'guid' parameter for design template loading.",
			);
			return;
		}

		this._renderLoading();
		this._run(designTemplateId, articleNumber, guid, resourceBaseUrl);
	}

	private _render(content: JSX.Element) {
		render(
			<StyleProvider theme={this._theme}>
				<GlobalStyle />
				{content}
			</StyleProvider>,
			document.getElementById(this._domRoot),
		);
	}

	private _renderLoading() {
		this._render(<ViewerLoading />);
	}

	private _renderError(message: string) {
		this._postMessageHandler.emit(OutgoingMessageEventType.VIEWER_ERROR, {
			data: { message },
		});
		this._render(<ViewerError message={message} />);
	}

	private _createARExportModel = (): Promise<Entity | undefined> => {
		if (!this._rootEntity) {
			return Promise.resolve(undefined);
		}
		this._arExportModel = this._rootEntity.clone();
		const bb = getEntityBoundingBox(this._rootEntity);
		if (bb) {
			const { center } = bb;
			this._arExportModel.translate(-center.x, 0, -center.z);
		}
		this._rootEntity.addChild(this._arExportModel);
		return Promise.resolve(this._arExportModel);
	};

	private _cleanupARExportModel = () => {
		this._arExportModel?.destroy();
		this._arExportModel = undefined;

		this._sceneEngine.autoRenderManager.pushSingleFrameRequest();
	};

	private _renderCanvas() {
		if (this._arConfig && this._rootEntity) {
			const arProviderProps: ARProviderProps = {
				...this._arConfig,
				exportModel: this._rootEntity,
				onInitExport: this._createARExportModel,
				onExportDone: this._cleanupARExportModel,
				generatePageUrl: () => window.location.href,
			};
			this._render(
				<ARProvider {...arProviderProps}>
					<ViewerCanvas onCanvasCreated={this._onCanvasCreated} showArButton />
					<ARRootWrapper />
				</ARProvider>,
			);
		} else {
			this._render(<ViewerCanvas onCanvasCreated={this._onCanvasCreated} />);
		}
	}

	private _onCanvasCreated = (placeholder: HTMLCanvasElement) => {
		const wrapper = placeholder.parentElement;
		if (wrapper) {
			this._canvas.style.width = "100%";
			this._canvas.style.height = "100%";
			this._canvas.style.background = "transparent";
			placeholder.remove();
			wrapper.prepend(this._canvas);
		}
	};

	private async _run(
		designTemplateId: string | null,
		articleNumber: string | null,
		guid: string | null,
		resourceBaseUrl: string,
	) {
		try {
			if (designTemplateId) {
				await this._openDesign(designTemplateId, guid!);
			} else if (articleNumber) {
				const errorMessage = this._setupProductFromArticleNumber(
					articleNumber,
					this._configurator,
				);
				if (errorMessage !== undefined) {
					this._renderError(errorMessage);
					return;
				}
			}
			await this._initScene(resourceBaseUrl);
			this._renderCanvas();
		} catch (error) {
			this._renderError(
				error instanceof Error ? error.message : JSON.stringify(error),
			);
		}
	}

	private async _openDesign(designId: string, guid: string): Promise<void> {
		let response: OpenResponse | undefined;
		try {
			response = await queryClient.fetchQuery<
				unknown,
				ApiError,
				OpenResponse,
				string[]
			>({
				queryKey: ["open", guid, designId],
				queryFn: () => openRequest(guid, null, designId),
			});
		} catch (error) {
			throw new Error(
				`Failed to load design "${designId}": ${error instanceof Error ? error.message : String(error)}`,
			);
		}

		const { configuration } = response.data;
		const deserialized = _migrateAndDeserialize(configuration);
		// All stored-configuration restores go through restoreStoredConfiguration
		// — see ADR-0005 and the function's source comment. A direct
		// configurator.set(root, deserialized) here would lose per-slot state
		// to Grythyttan's rule cascade (the bug that motivated ADR-0005).
		restoreStoredConfiguration(this._configurator, deserialized);
	}

	private _getConfigurationValue: GetConfigurationValue = <T,>(
		value: ConfigurationValue<T>,
		archetype: Property,
	): T => {
		if (!isProperty(value)) {
			return value;
		}
		const resolvedProperty = resolveProperty(value, archetype);
		return this._configurator.get(resolvedProperty).value as T;
	};

	private async _initScene(resourceBaseUrl: string) {
		// Init SceneEngine
		const assetBaseUrl = makeResourceUrl(
			appConfig.playCanvasAssetPath,
			resourceBaseUrl,
		);
		const configUrl = joinPaths(assetBaseUrl, appConfig.playCanvasConfigPath);
		const sceneEngineOptions: SceneEngineOptions = Object.assign(
			{},
			sceneConfig.sceneEngineOptions,
			{
				assetPrefix: joinPaths(
					assetBaseUrl,
					sceneConfig.sceneEngineOptions.assetPrefix || "",
				),
			},
		);
		await initSceneEngine(
			this._sceneEngine,
			webgpuRequested,
			this._canvas,
			configUrl,
			scenePath,
			sceneEngineOptions,
		);
		const app = this._sceneEngine.Application;
		app.root.name = "RootNode";

		// Hide the main scene's static backdrop sphere so the viewer renders
		// against a transparent page background. ADR-0003 covers why we do this
		// in code rather than via a separate viewer-only scene asset.
		const sphereBG = app.root.findByName("SphereBG") as Entity | null;
		if (sphereBG) {
			sphereBG.enabled = false;
		}

		const cameraSystem = this._createCamera();
		const sceneBuilder = this._createSceneBuilder();
		this._progressiveAA = new ProgressiveAA(app, cameraSystem.cameraComponent);

		await sceneBuilder.asyncOperationsFinished();
		// asyncOperationsFinished() covers only promises the scene-builder
		// explicitly tracked. PlayCanvas may still be fetching textures/materials
		// that were kicked off internally (e.g. non-preload assets on un-switched
		// mesh instances). Drain the registry completely before rendering.
		await waitForRegistryIdle(app.assets);
		// The scene tree contains every product (table + chairs + benches) with
		// visibility toggled by `viewerMode` rules. Walking all mesh instances
		// would frame the hidden geometry too, so we only look at entities that
		// are actually rendering.
		const modelAABB = getEntityBoundingBox(sceneBuilder.rootEntity);

		this._cameraFitter = new ViewerCameraFitter({
			cameraSystem,
			sceneEngine: this._sceneEngine,
			canvas: this._canvas,
			bbox: modelAABB,
			vFov: cameraSettings.fov,
			orbit: viewerInitialOrbit,
			paddingFactor: viewerCameraPaddingFactor,
			maxDistancePadding: viewerCameraMaxDistancePadding,
		});
		this._cameraFitter.fit();

		this._rootEntity = sceneBuilder.rootEntity;
	}

	private _createSceneBuilder() {
		const app = this._sceneEngine.Application;

		const sceneTree = (Definitions.Scene.scene as unknown as SceneStructure)
			.tree as SceneTree;
		const sceneBuilder = new SceneBuilder(
			sceneTree,
			root,
			this._getConfigurationValue,
		);

		const modelTypes = ["model", "render", "container", "template"];
		sceneBuilder.assets = app.assets
			.filter(
				(a) =>
					modelTypes.includes(a.type) &&
					!a.name.toLowerCase().endsWith(".model"),
			)
			.sort((a, b) => a.name.localeCompare(b.name));

		sceneBuilder.buildScene();
		app.root.addChild(sceneBuilder.rootEntity);

		return sceneBuilder;
	}

	private _createCamera() {
		const se = this._sceneEngine;
		const app = se.Application;

		let cameraEntity = app.root.findByName("Camera") as Entity | null;
		if (!cameraEntity) {
			cameraEntity = new Entity("Camera");
			cameraEntity.addComponent("camera");
			app.root.addChild(cameraEntity);
		}
		const device = app.graphicsDevice;
		const cameraView = new View();
		return new CameraSystem(
			cameraEntity,
			this._canvas,
			{ width: device.width, height: device.height },
			cameraView,
			Vec3.ZERO.clone(),
			cameraSettings,
			se.autoRenderManager,
			() => {},
		);
	}
}

async function waitForRegistryIdle(assets: AssetRegistry): Promise<void> {
	const loading = assets.filter((a) => a.loading);
	if (loading.length === 0) {
		return;
	}
	const whenLoaded = loading.map(
		(a) => new Promise<void>((resolve) => a.ready(() => resolve())),
	);
	await Promise.all(whenLoaded);
	await waitForRegistryIdle(assets);
}

function getEntityBoundingBox(entity: Entity): BoundingBox {
	const aabb = new BoundingBox();
	let initialized = false;

	const visit = (node: Entity) => {
		if (!node.enabled) {
			return;
		}
		const model = node.model;
		if (model?.enabled && model.meshInstances?.length) {
			for (const mi of model.meshInstances) {
				if (mi.visible === false) {
					continue;
				}
				if (!initialized) {
					aabb.copy(mi.aabb);
					initialized = true;
				} else {
					aabb.add(mi.aabb);
				}
			}
		}
		for (const child of node.children) {
			visit(child as Entity);
		}
	};

	visit(entity);
	return initialized ? aabb : new BoundingBox();
}

const ViewerLoading: FunctionComponent = () => (
	<Styled.Viewport data-variant="loading">
		<ProgressBar type="circular" size={140} />
	</Styled.Viewport>
);

const ViewerError: FunctionComponent<{ message: string }> = ({ message }) => (
	<Styled.Viewport data-variant="overlay">
		<Styled.ErrorContent>
			<Styled.ErrorTitle>Failed to load viewer</Styled.ErrorTitle>
			<Styled.ErrorMessage>{message}</Styled.ErrorMessage>
		</Styled.ErrorContent>
	</Styled.Viewport>
);

const ViewerCanvas: FunctionComponent<{
	onCanvasCreated: (canvas: HTMLCanvasElement) => void;
	showArButton?: boolean;
}> = ({ onCanvasCreated, showArButton }) => {
	const ref = useCallback(
		(canvas: HTMLCanvasElement | null) => {
			if (canvas) {
				onCanvasCreated(canvas);
			}
		},
		[onCanvasCreated],
	);

	return (
		<Styled.Viewport data-variant="canvas">
			<Styled.Canvas ref={ref} />
			{showArButton && (
				<Styled.ARButtonContainer>
					<ARButton icon="ar-2" appearance="solid" color="neutral" rounded />
				</Styled.ARButtonContainer>
			)}
		</Styled.Viewport>
	);
};
